import React, { useState, useEffect, useRef } from 'react';
import { Check, Copy, X, Mail, Inbox, PartyPopper, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Step = ({ n, children }) => (
  <li className="flex items-start gap-2.5">
    <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5">{n}</span>
    <span className="text-sm text-zinc-600 leading-relaxed">{children}</span>
  </li>
);

const EmailAssistantGuidedSetup = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [address, setAddress] = useState('');
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState({});
  const pollRef = useRef(null);

  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('streakfree_token')}` });

  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/api/email-assistant/address`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setAddress(d.email_address)).catch(() => {});
  }, [open]);

  // Poll for the Gmail confirmation code while on step 1
  useEffect(() => {
    if (!open || step !== 1) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    const poll = async () => {
      try {
        const r = await fetch(`${API_URL}/api/email-assistant/setup-status`, { headers: authHeaders() });
        if (r.ok) setStatus(await r.json());
      } catch (e) { /* ignore */ }
    };
    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, step]);

  if (!open) return null;

  const copyAddress = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const titles = [
    { icon: Mail, title: 'Copy your address', sub: 'Step 1 of 3' },
    { icon: Inbox, title: 'Add it in Gmail', sub: 'Step 2 of 3 — we watch for the code' },
    { icon: PartyPopper, title: 'Finish up', sub: 'Step 3 of 3' },
  ];
  const T = titles[step]; const Icon = T.icon;

  return (
    <div className="fixed inset-0 z-[210] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" data-testid="ea-guided-setup">
      <div className="w-full max-w-md bg-white sm:rounded-2xl rounded-t-3xl shadow-xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
            <Icon size={22} className="text-sky-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-zinc-900">{T.title}</h2>
            <p className="text-xs text-zinc-500">{T.sub}</p>
          </div>
          <div className="flex gap-1 mr-1">
            {[0,1,2].map(i => (
              <div key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-5 bg-sky-500' : i < step ? 'w-1.5 bg-sky-300' : 'w-1.5 bg-zinc-200'}`} />
            ))}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-lg" data-testid="ea-guided-close">
            <X size={18} className="text-zinc-400" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {step === 0 && (
            <>
              <p className="text-sm text-zinc-600">This is your unique StreakFree address. Your email keeps working exactly as before — a copy just flows through StreakFree so we can answer "when's my next clean?" for you.</p>
              <div className="flex gap-2">
                <input type="text" readOnly value={address}
                  className="flex-1 px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-xs font-mono text-zinc-700 truncate"
                  data-testid="ea-guided-address" />
                <button onClick={copyAddress}
                  className={`px-3.5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-1.5 ${copied ? 'bg-emerald-500 text-white' : 'bg-zinc-900 text-white'}`}
                  data-testid="ea-guided-copy">
                  <Copy size={13} /> {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <button onClick={() => setStep(1)} disabled={!address}
                className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white text-sm font-bold"
                data-testid="ea-guided-next-1">
                I've copied it — next
              </button>
            </>
          )}

          {step === 1 && (
            <>
              <ol className="space-y-2.5">
                <Step n={1}>Open <strong>Gmail</strong> → <strong>⚙ gear</strong> → <strong>See all settings</strong></Step>
                <Step n={2}>Go to <strong>Forwarding and POP/IMAP</strong> → <strong>Add a forwarding address</strong></Step>
                <Step n={3}>Paste the address and click <strong>Next → Proceed</strong></Step>
              </ol>
              <div className={`p-4 rounded-xl border-2 ${status.confirmation_code ? 'bg-emerald-50 border-emerald-300' : 'bg-zinc-50 border-dashed border-zinc-300'}`} data-testid="ea-guided-code-box">
                {status.confirmation_code ? (
                  <>
                    <p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1.5"><Check size={14} /> Code received!</p>
                    <p className="text-2xl font-extrabold tracking-widest text-emerald-800" data-testid="ea-guided-code">{status.confirmation_code}</p>
                    <p className="text-[11px] text-emerald-700 mt-1.5">Type this into Gmail's confirmation box{status.approve_link ? ' — or ' : '.'}
                      {status.approve_link && <a href={status.approve_link} target="_blank" rel="noreferrer" className="font-semibold underline inline-flex items-center gap-0.5">approve with one click <ExternalLink size={10} /></a>}
                    </p>
                  </>
                ) : (
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-sky-400 animate-pulse flex-shrink-0" />
                    <p className="text-xs text-zinc-500">Waiting for Gmail's confirmation code — it'll appear right here within a minute of you clicking Proceed…</p>
                  </div>
                )}
              </div>
              <button onClick={() => setStep(2)}
                className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold"
                data-testid="ea-guided-next-2">
                {status.confirmation_code ? "Done — I've confirmed it" : 'Skip — my provider works differently'}
              </button>
              <p className="text-[11px] text-zinc-400 text-center">Outlook / other: Settings → Mail → Forwarding — no code needed, just save.</p>
            </>
          )}

          {step === 2 && (
            <>
              <ol className="space-y-2.5">
                <Step n={1}>Back in Gmail, click the <strong>search box options icon</strong> (sliders, right side of the search bar) to open <strong>filter options</strong></Step>
                <Step n={2}>In the <strong>To</strong> field type <strong>your own email address</strong>, then click <strong>Create filter</strong></Step>
                <Step n={3}>Tick <strong>"Forward it to"</strong> and pick your StreakFree address from the list, then click <strong>Create filter</strong> — done!</Step>
              </ol>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="ea-guided-filter-note">
                <p className="text-xs text-amber-800 leading-relaxed"><strong>Why a filter and not the main "Forward a copy" dropdown?</strong> Gmail's main dropdown only allows one forwarding address — a filter works the same but lets you keep any forwarding you already have. Your inbox keeps its copy either way.</p>
              </div>
              <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl">
                <p className="text-xs text-sky-800 leading-relaxed">From now on, when a customer emails asking when their next clean is due, StreakFree replies within a minute with an <strong>"on or around"</strong> date — branded as your business. Everything else is left alone.</p>
              </div>
              <button onClick={onClose}
                className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold"
                data-testid="ea-guided-finish">
                Finish
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailAssistantGuidedSetup;
